import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';

import type { JwtPayload } from '@/lib/api/types';

interface AuthUser {
  sub: string;
  email: string;
  tenant_id: string;
  roles: string[];
  permissions: string[];
  exp: number;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isHydrated: boolean;
  setToken: (token: string) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  isExpired: () => boolean;
}

function decode(token: string): AuthUser | null {
  try {
    const p = jwtDecode<JwtPayload>(token);
    return {
      sub: p.sub,
      email: p.email,
      tenant_id: p.tenant_id,
      roles: p.roles ?? [],
      permissions: p.permissions ?? [],
      exp: p.exp,
    };
  } catch {
    return null;
  }
}

/**
 * Auth store — JWT-only, stateless.
 * tenant_id is read from the JWT payload, NEVER from local user input.
 * (See Technical Architecture §2.1.)
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isHydrated: false,

      setToken: (token) => {
        const user = decode(token);
        if (!user) {
          set({ token: null, user: null });
          return;
        }
        set({ token, user });
      },

      logout: () => set({ token: null, user: null }),

      hasPermission: (permission) =>
        get().user?.permissions.includes(permission) ?? false,

      hasRole: (role) => get().user?.roles.includes(role) ?? false,

      isExpired: () => {
        const exp = get().user?.exp;
        if (!exp) return true;
        return Date.now() >= exp * 1000;
      },
    }),
    {
      name: 'cx-web.auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ token: s.token, user: s.user }),
      onRehydrateStorage: () => (state) => {
        if (state) state.isHydrated = true;
      },
    },
  ),
);
